import authApi from '@api/authApi';
import IconLocation from '@assets/icons/location.svg?react';
import IconLogbook from '@assets/icons/logbook.svg?react';
import DefaultPage from '@components/defaultPage';
import MenuList from '@components/menuList';
import MenuListItem from '@components/menuListItem';
import Title from '@components/title';
import UserTile from '@components/userTile';
import useAuth from '@hooks/useAuth';
import useFeatureFlagOrClaim from '@hooks/useFeatureFlag';
import { useTranslator } from '@i18n';
import { Group } from '@mantine/core';
import ColorSchemeToggle from '@routes/profile/-colorSchemeToggle';
import MaintenanceModeButton from '@routes/profile/-maintenanceModeButton';
import { IconBinaryTree, IconLink, IconLogout, IconSettings, IconUser } from '@tabler/icons-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import isTruthy from '@util/isTruthy';

export const Route = createFileRoute('/profile/')({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslator();
  const { user, hasClaim } = useAuth();
  const canSeeOrgs = hasClaim('orgs/read', 'any');
  const canSeeUsers = hasClaim('users/read', 'any');
  const canSeeLocations = hasClaim('locations/read', 'any');
  const showDashboard = useFeatureFlagOrClaim('FEATURE_DASHBOARD', 'global');
  const navigate = useNavigate();

  const links = [
    canSeeOrgs && (
      <MenuListItem key="orgs" to="/profile/orgs" icon={<IconBinaryTree size={16} />} label={t('orgs.link')} />
    ),
    canSeeLocations && (
      <MenuListItem
        key="locations"
        to="/profile/locations"
        icon={<IconLocation size={16} />}
        label={t('locations.link')}
      />
    ),
    canSeeUsers && (
      <MenuListItem key="users" to="/profile/users" icon={<IconUser size={16} />} label={t('users.link')} />
    ),
    showDashboard && <MenuListItem key="logs" to="/log" icon={<IconLogbook size={16} />} label={t('log.link')} />,
  ].filter(isTruthy);

  const buttons = [
    config.FEATURE_MAINTENANCE_MODE && <MaintenanceModeButton key="maintenance" />,
    config.FEATURE_LIGHT_MODE && <ColorSchemeToggle key="colorScheme" />,
  ];

  return (
    <Title title={t('profile.title')}>
      <DefaultPage
        css={{
          display: 'grid',
          alignContent: 'start',
          gap: 'var(--mantine-spacing-md)',
          paddingBottom: 50,
        }}
      >
        {user && (
          <UserTile
            user={user}
            css={{
              margin: 'var(--mantine-spacing-md) 0',
            }}
          />
        )}

        {links.length > 0 && <MenuList dense>{links}</MenuList>}

        {buttons.length > 0 && <MenuList dense>{buttons}</MenuList>}

        <MenuList dense>
          <MenuListItem to="/profile/settings" icon={<IconSettings size={16} />} label={t('settings.link')} />

          <MenuListItem
            onClick={() => {
              authApi.logout();
              navigate({ to: '/', search: { locationId: undefined }, params: false });
            }}
            icon={<IconLogout size={16} />}
            label={t('common.logout')}
          />
        </MenuList>

        <Group
          pl="md"
          css={{
            fontSize: 'var(--mantine-font-size-sm)',
            color: 'var(--mantine-color-dimmed)',
          }}
        >
          <span>
            {config.VERSION} &copy; {config.BUILDTIME.getFullYear()}
          </span>

          <a
            href={`${config.BACKEND_WEBSITE}/changelog`}
            target="_blank"
            rel="noopener noreferrer"
            css={{
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: 'inherit',
            }}
          >
            <IconLink size="1em" />
            {t('profile.changelog')}
          </a>
        </Group>
      </DefaultPage>
    </Title>
  );
}
